import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { CalendarClock, Copy, CreditCard, DollarSign, ExternalLink, Loader2, Wallet, X } from "lucide-react";

/* ----------------------- Payments (Stripe) view -----------------------
 * One-tap deep-links to the Stripe Dashboard + a thin summary card of
 * Memory Lane bookings (count by status, recent transactions). Useful for
 * Ashlee to confirm a charge landed without leaving the app.
 *
 * The summary endpoint reads payment_transactions (our source of truth),
 * which the success-page poll + Stripe webhook keep in sync.
 */
export default function PaymentsView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let active = true;
        api.get("/admin/payments/summary").then((r) => {
            if (!active) return;
            setData(r.data);
        }).catch((e) => {
            if (!active) return;
            setErr(e?.response?.data?.detail || "Couldn't load payments.");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, []);

    const live = data?.is_live_mode;

    return (
        <section data-testid="admin-payments">
            <header className="mb-6 flex items-start gap-4 justify-between flex-wrap">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl text-[#12776F] inline-flex items-center gap-2">
                        <Wallet size={22} strokeWidth={1.7} className="text-teal-dark" />
                        Payments (Stripe)
                    </h1>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                        Quick look at recent paid bookings + one-tap links into your Stripe Dashboard.
                        Memory Lane only ever holds <em>this</em> snapshot — the source of truth lives in Stripe.
                    </p>
                </div>
                <span
                    data-testid="payments-mode-badge"
                    className={`text-xs font-semibold px-3 py-1.5 rounded-2xl uppercase tracking-wider ${live
                        ? "bg-stage_early/60 text-stage_early-dark"
                        : "bg-yellow-soft text-yellow-dark"
                        }`}
                >
                    {live ? "🟢 Live mode" : "🟡 Test mode (sk_test)"}
                </span>
            </header>

            {!live && (
                <div className="ml-card p-4 mb-5 bg-yellow-soft/40 border border-yellow-soft" data-testid="payments-live-warning">
                    <p className="text-sm text-[#1F2624]">
                        <strong>You&apos;re in test mode.</strong> Real customer cards won&apos;t work until your production
                        environment has a live <code className="font-mono text-xs">STRIPE_API_KEY</code> set.
                        See the &ldquo;Production Setup&rdquo; guide at the bottom of this page.
                    </p>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </p>
            ) : err ? (
                <p className="text-sm text-coral-dark">{err}</p>
            ) : (
                <>
                    {/* Quick stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="payments-stats">
                        <PaymentStatCard icon={DollarSign} label="Recent revenue" value={`$${((data.recent_paid_amount_cents || 0) / 100).toFixed(2)}`} tone="teal" />
                        <PaymentStatCard icon={CreditCard} label="Paid bookings" value={data.counts?.paid || 0} tone="green" />
                        <PaymentStatCard icon={CalendarClock} label="Pending" value={data.counts?.pending || 0} tone="yellow" />
                        <PaymentStatCard icon={X} label="Expired" value={data.counts?.expired || 0} tone="muted" />
                    </div>

                    {/* Deep-link cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="payments-deep-links">
                        <DeepLink
                            href="https://dashboard.stripe.com/payments"
                            title="All payments"
                            blurb="See every charge, refund, and dispute in real time."
                            testid="payments-link-payments"
                        />
                        <DeepLink
                            href="https://dashboard.stripe.com/customers"
                            title="Customers"
                            blurb="Look up a caregiver by email after they book."
                            testid="payments-link-customers"
                        />
                        <DeepLink
                            href="https://dashboard.stripe.com/payouts"
                            title="Payouts"
                            blurb="Track when Stripe sends money to your bank."
                            testid="payments-link-payouts"
                        />
                        <DeepLink
                            href="https://dashboard.stripe.com/apikeys"
                            title="API keys"
                            blurb="Rotate keys, manage restricted keys."
                            testid="payments-link-apikeys"
                        />
                        <DeepLink
                            href="https://dashboard.stripe.com/webhooks"
                            title="Webhooks"
                            blurb="Verify your webhook is firing on every charge."
                            testid="payments-link-webhooks"
                        />
                        <DeepLink
                            href="https://dashboard.stripe.com/settings/account"
                            title="Account settings"
                            blurb="Business info, branding, tax IDs, bank account."
                            testid="payments-link-settings"
                        />
                    </div>

                    {/* Recent transactions */}
                    <div className="ml-card p-5 md:p-6 bg-white" data-testid="payments-recent">
                        <h2 className="font-heading text-lg text-[#1F2624] mb-3">Most recent transactions</h2>
                        {(data.recent || []).length === 0 ? (
                            <p className="text-sm text-[#6E7A74] italic">No transactions yet. When a caregiver books, they&apos;ll appear here.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                            <th className="py-2 pr-4">When</th>
                                            <th className="py-2 pr-4">Package</th>
                                            <th className="py-2 pr-4">Email</th>
                                            <th className="py-2 pr-4">Amount</th>
                                            <th className="py-2 pr-4">Status</th>
                                            <th className="py-2">Stripe</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.recent.map((r) => (
                                            <tr key={r.session_id} className="border-b border-cream-dark" data-testid={`payments-row-${(r.session_id || "").slice(0, 12)}`}>
                                                <td className="py-2 pr-4 text-[#4C5551]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                                                <td className="py-2 pr-4 text-[#1F2624]">{r.package_id ? `${r.package_id}-min` : "—"}</td>
                                                <td className="py-2 pr-4 text-[#1F2624] truncate max-w-[180px]">{r.contact_email || "—"}</td>
                                                <td className="py-2 pr-4 text-[#1F2624] font-mono text-xs">
                                                    {r.amount ? `$${(r.amount / 100).toFixed(2)}` : "—"}
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-2xl ${r.payment_status === "paid"
                                                        ? "bg-stage_early/60 text-stage_early-dark"
                                                        : r.payment_status === "pending"
                                                            ? "bg-yellow-soft text-yellow-dark"
                                                            : "bg-cream-dark text-[#4C5551]"
                                                        }`}>
                                                        {r.payment_status}
                                                    </span>
                                                </td>
                                                <td className="py-2">
                                                    <a
                                                        href={`https://dashboard.stripe.com/payments/${r.session_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1"
                                                        data-testid={`payments-stripe-link-${(r.session_id || "").slice(0, 12)}`}
                                                    >
                                                        Open <ExternalLink size={11} strokeWidth={1.8} />
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Production setup */}
                    <details
                        className="ml-card p-5 md:p-6 bg-cream-light mt-5"
                        data-testid="payments-production-setup"
                        open={!live}
                    >
                        <summary className="font-heading text-lg text-[#1F2624] cursor-pointer">
                            🚀 Production setup checklist
                        </summary>
                        <ol className="mt-4 space-y-3 text-sm text-[#1F2624] list-decimal pl-5">
                            <li>
                                <strong>Activate your Stripe account.</strong> Dashboard → Activate. Stripe asks for tax info, bank
                                account, and your business description (use &ldquo;Care navigation consultations&rdquo;).
                            </li>
                            <li>
                                <strong>Roll & set the live secret key.</strong> Dashboard → API keys → Roll → copy the new
                                <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mx-1">sk_live_…</code>.
                                In Emergent Deploy → Project Settings → Environment Variables, set
                                <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mx-1">STRIPE_API_KEY</code>
                                and redeploy. (Test mode here stays on <code className="font-mono text-xs">sk_test_emergent</code>.)
                            </li>
                            <li>
                                <strong>Add a webhook so the success page can confirm payment without polling.</strong> Dashboard → Webhooks
                                → &ldquo;Add endpoint&rdquo; → URL: <code className="font-mono text-xs">https://memorylanecare.net/api/webhook/stripe</code>.
                                Event: <code className="font-mono text-xs">checkout.session.completed</code>. Copy the signing
                                secret and add it as <code className="font-mono text-xs">STRIPE_WEBHOOK_SECRET</code> in env vars.
                            </li>
                            <li>
                                <strong>Verify your Calendly URLs.</strong> The backend env reads
                                <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mx-1">CALENDLY_30_URL</code> and
                                <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mx-1">CALENDLY_60_URL</code> —
                                set both to your real event URLs in production env.
                            </li>
                            <li>
                                <strong>Test a $1 charge.</strong> In live mode, book a 30-min session yourself with a real
                                card, then refund yourself from Dashboard → Payments. Confirms the full loop works.
                            </li>
                        </ol>
                    </details>
                </>
            )}
        </section>
    );
}

function PaymentStatCard({ icon: Icon, label, value, tone }) {
    const tones = {
        teal: "bg-teal-soft text-teal-dark",
        green: "bg-stage_early/60 text-stage_early-dark",
        yellow: "bg-yellow-soft text-yellow-dark",
        muted: "bg-cream-light text-[#4C5551]",
    };
    return (
        <div className={`rounded-2xl p-4 ${tones[tone] || tones.muted}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} strokeWidth={1.8} />
                <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
            </div>
            <p className="text-xl font-heading">{value}</p>
        </div>
    );
}

function DeepLink({ href, title, blurb, testid }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            className="ml-card ml-card-hover p-4 bg-white block group"
        >
            <p className="font-heading text-base text-[#1F2624] inline-flex items-center gap-1.5">
                {title}
                <ExternalLink size={12} strokeWidth={1.8} className="text-teal-dark opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-xs text-[#4C5551] mt-1 leading-relaxed">{blurb}</p>
        </a>
    );
}


